import { Button } from '../Button'
import { Calendar } from '../Calendar'
import { DismissibleLayer } from '../DismissibleLayer'
import { FocusTrap } from '../FocusTrap'
import { Popper } from '../Popper'
import { Portal } from '../Portal'
import { Presence } from '../Presence'

interface CalendarDialogProps {
  date: string | null;
  dialogRef: React.RefObject<HTMLDivElement>;
  id: string;
  max?: string | number;
  min?: string | number;
  onDateChange: (date: Date | null) => void;
  portalContainer: HTMLElement;
  setShowCalendar: (showCalendar: boolean) => void;
  showCalendar: boolean;
  withPortal: boolean;
}

export function CalendarDialog ({
  date,
  id,
  dialogRef,
  max,
  min,
  onDateChange,
  portalContainer,
  setShowCalendar,
  showCalendar,
  withPortal,
}: CalendarDialogProps) {
  return (
    <Portal enabled={withPortal} container={portalContainer}>
      <FocusTrap
        active={showCalendar}
        asChild
        setInitialFocusOn='button[data-focused="true"]'
      >
        <Presence present={showCalendar} asChild>
          <DismissibleLayer
            asChild
            onEscapeKey={() => {
              setShowCalendar(false)
            }}
            onFocusOutside={() => {
              setShowCalendar(false)
            }}
            onPointerOutside={() => {
              setShowCalendar(false)
            }}
          >
            <Popper.Content
              align='end'
              className='bg-white rounded-lg border border-gray-200 shadow-default data-[side=top]:animate-slide-up-and-fade-in data-[side=bottom]:animate-slide-down-and-fade-in'
              id={id}
              ref={dialogRef}
              role='dialog'
              side='top'
              sideOffset={6}
            >
              <div className='p-2'>
                <Calendar
                  data-testid='calendar'
                  date={date}
                // The `<DateInput />` extends the native `<input type="date">`
                // element, which is a confusing HTML element to type because
                // multiple input `types` accept the same attributes but expect
                // different shapes of values. React's TS types are broad to
                // support all input `type`s and we stick with the React types
                // in the `<DateInput />` for maximum compatibility with
                // external libraries. But this means we need to do some type
                // narrowing before passing these props to components like the
                // `<Calendar />` that are typed based on what they _actually_
                // expect.
                //
                  maxDate={typeof max === 'string' ? max : undefined}
                  minDate={typeof min === 'string' ? min : undefined}
                  onDateChange={onDateChange}
                />
              </div>
              {date && (
                <div className='flex justify-end p-2 border-t border-gray-200'>
                  <Button
                    onClick={() => {
                      onDateChange(null)
                      setShowCalendar(false)
                    }}
                    variant='tertiary-deemphasized'
                  >
                    Clear
                  </Button>
                </div>
              )}
            </Popper.Content>
          </DismissibleLayer>
        </Presence>
      </FocusTrap>
    </Portal>
  )
}
